import { Point as OlPoint, LineString as OlLineString, Polygon as OlPolygon, } from "ol/geom";
import { Extent } from "./Extent";
import { EsriJSON as OlEsriJSON, GeoJSON as OlGeoJSON } from "ol/format";
export var GeometryType;
(function (GeometryType) {
    GeometryType["Point"] = "Point";
    GeometryType["LineString"] = "LineString";
    GeometryType["LinearRing"] = "LinearRing";
    GeometryType["Polygon"] = "Polygon";
    GeometryType["MultiPoint"] = "MultiPoint";
    GeometryType["MultiLineString"] = "MultiLineString";
    GeometryType["MultiPolygon"] = "MultiPolygon";
    GeometryType["GeometryCollection"] = "GeometryCollection";
    GeometryType["Circle"] = "Circle";
    GeometryType["Rectangle"] = "Rectangle";
})(GeometryType || (GeometryType = {}));
export var FormatType;
(function (FormatType) {
    FormatType["GeoJSON"] = "GeoJSON";
    FormatType["EsriJSON"] = "EsriJSON";
})(FormatType || (FormatType = {}));
export class Geometry {
    _olGeom;
    _crs;
    constructor(data, crs, format = FormatType.GeoJSON) {
        if (typeof data === "string" || data?.constructor === Object) {
            let olFormat = null;
            if (format === FormatType.GeoJSON) {
                olFormat = new OlGeoJSON();
            }
            else if (format === FormatType.EsriJSON) {
                olFormat = new OlEsriJSON();
            }
            this._olGeom = olFormat.readGeometry(data);
        }
        else {
            this._olGeom = data;
        }
        this._crs = crs;
    }
    static fromOl(geometry, crs) {
        return new Geometry(geometry, crs);
    }
    static typeFromEsri(esriType) {
        let detected = esriType.replace("esriGeometry", "");
        detected = detected === "Polyline" ? "LineString" : detected;
        const type = detected;
        if (!Object.values(GeometryType).includes(type)) {
            console.error(`Unknown geometry type '${type}' generated from '${esriType}'`);
            return;
        }
        return type;
    }
    getType() {
        return this._olGeom.getType();
    }
    getCrs() {
        return this._crs;
    }
    getEsriType() {
        if (this.isPolygon()) {
            return "esriGeometryPolygon";
        }
        else if (this.isLineString()) {
            return "esriGeometryPolyline";
        }
        else if (this.isPoint()) {
            return "esriGeometryPoint";
        }
    }
    isPoint() {
        return this._olGeom instanceof OlPoint;
    }
    toPoint() {
        return this._olGeom;
    }
    isLineString() {
        return this._olGeom instanceof OlLineString;
    }
    toLineString() {
        return this._olGeom;
    }
    isPolygon() {
        return this._olGeom instanceof OlPolygon;
    }
    toPolygon() {
        return this._olGeom;
    }
    toSimpleGeometries() {
        const geometries = [];
        if (this.isSimple()) {
            geometries.push(this);
        }
        else {
            switch (this.getType()) {
                case GeometryType.GeometryCollection:
                    const collection = this._olGeom;
                    collection.getGeometries().forEach((geom) => {
                        const geometry = Geometry.fromOl(geom, this._crs);
                        if (geometry.isSimple()) {
                            geometries.push(geometry);
                        }
                        else {
                            geometry.toSimpleGeometries().forEach((geometry) => {
                                geometries.push(geometry);
                            });
                        }
                    });
                    break;
                case GeometryType.MultiPoint:
                    const multiPoint = this._olGeom;
                    multiPoint.getPoints().forEach((point) => {
                        geometries.push(Geometry.fromOl(point, this._crs));
                    });
                    break;
                case GeometryType.MultiLineString:
                    const multiLineString = this._olGeom;
                    multiLineString.getLineStrings().forEach((lineString) => {
                        geometries.push(Geometry.fromOl(lineString, this._crs));
                    });
                    break;
                case GeometryType.MultiPolygon:
                    const multiPolygon = this._olGeom;
                    multiPolygon.getPolygons().forEach((polygon) => {
                        geometries.push(Geometry.fromOl(polygon, this._crs));
                    });
                    break;
            }
        }
        return geometries;
    }
    isSimple() {
        return [
            GeometryType.Point,
            GeometryType.LineString,
            GeometryType.Polygon,
        ].includes(this.getType());
    }
    getVertexes() {
        if (this.isPoint()) {
            return [this.toPoint().getCoordinates()];
        }
        else if (this.isLineString()) {
            return this.toLineString().getCoordinates();
        }
        else if (this.isPolygon()) {
            return this.toPolygon().getCoordinates()[0];
        }
        else {
            return [];
        }
    }
    getLength() {
        if (this.isLineString()) {
            return this.toLineString().getLength();
        }
        else if (this.isPolygon()) {
            const linestring = new OlLineString(this.toPolygon().getLinearRing(0).getCoordinates(), "XY");
            return linestring.getLength();
        }
        else {
            return 0;
        }
    }
    getArea() {
        if (this.isPolygon()) {
            return this.toPolygon().getArea();
        }
        else {
            return 0;
        }
    }
    getCentroid() {
        if (this.isPolygon()) {
            const interiorPoint = this.toPolygon().getInteriorPoint();
            return [
                interiorPoint.getCoordinates()[0],
                interiorPoint.getCoordinates()[1],
            ];
        }
        else if (this.isLineString()) {
            return this.getPointAlong(0.5);
        }
        else if (this.isPoint()) {
            return this.toPoint().getCoordinates();
        }
    }
    getRotation() {
        if (!this.isLineString()) {
            return;
        }
        const lineString = this.toLineString();
        const start = lineString.getFirstCoordinate();
        const end = lineString.getLastCoordinate();
        const lx = end[0] - start[0];
        const ly = end[1] - start[1];
        const rotation = -1 * Math.atan2(ly, lx);
        if (rotation >= Math.PI / 2 || rotation < -Math.PI / 2) {
            return rotation - Math.PI;
        }
        return rotation;
    }
    getPointAlong(percentage) {
        if (!this.isLineString()) {
            return;
        }
        const lineCoordinates = this.toLineString().getCoordinates();
        let totalLength = 0;
        let previousPercentageSum = 0;
        let percentageSum = 0;
        let remainderPercentageSum = undefined;
        let len = undefined;
        const segmentLengths = [];
        let i = 1;
        let dx = undefined;
        let dy = undefined;
        let c = undefined;
        let c0 = undefined;
        let c1 = undefined;
        if (percentage === 0) {
            return [lineCoordinates[0][0], lineCoordinates[0][1]];
        }
        else if (percentage === 1) {
            i = lineCoordinates.length - 1;
            return [lineCoordinates[i][0], lineCoordinates[i][1]];
        }
        else {
            for (; i < lineCoordinates.length; i++) {
                dx = lineCoordinates[i][0] - lineCoordinates[i - 1][0];
                dy = lineCoordinates[i][1] - lineCoordinates[i - 1][1];
                len = Math.sqrt(dx * dx + dy * dy);
                segmentLengths.push(len);
                totalLength += len;
            }
            for (i = 0; i < segmentLengths.length && percentageSum < percentage; i++) {
                previousPercentageSum = percentageSum;
                percentageSum += segmentLengths[i] / totalLength;
            }
            remainderPercentageSum = percentage - previousPercentageSum;
            c0 = lineCoordinates[i - 1];
            c1 = lineCoordinates[i];
            c = [
                c0[0] + remainderPercentageSum * (c1[0] - c0[0]),
                c0[1] + remainderPercentageSum * (c1[1] - c0[1]),
            ];
            return c;
        }
    }
    getExtent() {
        const extent = this._olGeom.getExtent();
        return Extent.fromBbox(extent, this._crs);
    }
    getBbox(extendBy) {
        let bbox = [0, 0, 0, 0];
        if (this.isPoint()) {
            if (this.toPoint().getCoordinates()) {
                const crds = (this.toPoint().getCoordinates());
                bbox = [crds[0], crds[1], crds[0], crds[1]];
            }
        }
        else {
            bbox = this._olGeom.getExtent();
        }
        if (extendBy !== undefined) {
            bbox[0] -= extendBy;
            bbox[1] -= extendBy;
            bbox[2] += extendBy;
            bbox[3] += extendBy;
        }
        return bbox;
    }
    toOl() {
        return this._olGeom;
    }
    transform(to) {
        if (this._crs !== to) {
            const geom = new OlGeoJSON().readGeometry(new OlGeoJSON().writeGeometry(this._olGeom));
            return Geometry.fromOl(geom.transform(this._crs, to), to);
        }
        return this;
    }
    toWebMap() {
        if (this.isPoint()) {
            return `P${this.toPoint().getCoordinates().join(",")}`;
        }
        else if (this.isPolygon()) {
            const coords = [];
            const coordinates = this.toPolygon().getCoordinates();
            if (coordinates[0].length === 181) {
                const radius = this.getLength() / (2 * Math.PI);
                return `C${this.getCentroid().join(",")},${radius}`;
            }
            else {
                coordinates[0].forEach((xy) => {
                    coords.push(xy.join(","));
                });
                return `L${coords.join(";")}`;
            }
        }
        else if (this.isLineString()) {
            const coords = [];
            this.toLineString()
                .getCoordinates()
                .forEach((xy) => {
                coords.push(xy.join(","));
            });
            return `H${coords.join(";")}`;
        }
    }
    toEsri() {
        return new OlEsriJSON().writeGeometry(this._olGeom);
    }
    toGeoJSON(decimals = undefined) {
        return new OlGeoJSON().writeGeometry(this._olGeom, {
            decimals: decimals,
        });
    }
}
//# sourceMappingURL=Geometry.js.map